import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { api, TimbaTournamentDetail, TimbaTournamentRef } from '../api/client';
import { useStore } from '../stores/store';
import { getIdentity } from '../lib/identity';
import { TimbaBoard } from '../components/TimbaBoard';
import { BackLink, H2, Muted } from '../components/ui';

function inviteLink(token: string): string {
  return window.location.origin + (process.env.PUBLIC_URL || '') + '/timba/t/' + token;
}

// Copia texto: API moderna en contexto seguro, si no fallback legado (sirve sobre HTTP en la LAN).
async function copyText(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function MisTorneos() {
  const { token: joinToken } = useParams();
  const nick = useStore((s) => s.nick);
  const setNick = useStore((s) => s.setNick);
  const pub = (() => {
    try {
      return getIdentity().pub;
    } catch {
      return '';
    }
  })();

  const [mine, setMine] = useState<TimbaTournamentRef[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [detail, setDetail] = useState<TimbaTournamentDetail | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [name, setName] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [toast, setToast] = useState('');

  const flash = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(''), 3800);
  };

  // Solo torneos privados (el global vive en la home de la Timba).
  const reload = useCallback(async () => {
    if (!pub) return;
    const d = await api.timbaTournaments(pub).catch(() => null);
    if (!d) return;
    const privs = d.tournaments.filter((t) => !t.global);
    setMine(privs);
    setSelected((cur) => cur || (privs[0]?.id ?? ''));
  }, [pub]);

  // Carga + auto-unión si se entró por un link de invitación (/timba/t/:token).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (joinToken && pub) {
        const r = await api.timbaJoinTournament(pub, nick, joinToken).catch(() => null);
        if (r && alive) {
          if (r.nick && r.nick !== nick) setNick(r.nick);
          flash(`Te uniste a "${r.name}"`);
          setSelected(r.id);
        }
      }
      await reload();
    })();
    return () => {
      alive = false;
    };
  }, [joinToken, pub, nick, reload]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let alive = true;
    api.timbaTournament(selected, pub).then((d) => alive && setDetail(d)).catch(() => alive && setDetail(null));
    return () => {
      alive = false;
    };
  }, [selected, mine]);

  const create = async () => {
    const clean = name.trim();
    if (clean.length < 2) return;
    const r = await api.timbaCreateTournament(pub, nick, clean).catch(() => null);
    if (r) {
      const renamed = !!r.nick && r.nick !== nick;
      if (renamed) setNick(r.nick!);
      setName('');
      setShowCreate(false);
      await reload();
      setSelected(r.id);
      flash(renamed ? `Torneo "${r.name}" creado · tu nombre quedó "${r.nick}" (el elegido ya estaba en uso)` : `Torneo "${r.name}" creado`);
    }
  };

  const join = async () => {
    const tok = joinInput.trim().replace(/.*\/timba\/t\//, '');
    if (!tok) return;
    const r = await api.timbaJoinTournament(pub, nick, tok).catch(() => null);
    if (r) {
      const renamed = !!r.nick && r.nick !== nick;
      if (renamed) setNick(r.nick!);
      setJoinInput('');
      setShowJoin(false);
      await reload();
      setSelected(r.id);
      flash(renamed ? `Te uniste a "${r.name}" · tu nombre quedó "${r.nick}"` : `Te uniste a "${r.name}"`);
    } else {
      flash('No se encontró ese torneo');
    }
  };

  const selRef = mine.find((t) => t.id === selected);
  const share = async () => {
    if (!selRef?.token) return;
    const url = inviteLink(selRef.token);
    if (navigator.share) {
      try {
        await navigator.share({ title: `Timba — ${selRef.name}`, text: `Sumate a mi torneo "${selRef.name}" en VamoVamo!\n${url}` });
        return;
      } catch {
        return;
      }
    }
    const ok = await copyText(url);
    flash(ok ? '📋 ¡Invitación copiada! Pegásela a tus amigos por WhatsApp y que se sumen a tu timba. 🎉' : `Copiá y compartí: ${url}`);
  };

  return (
    <>
      <BackLink onClick={() => window.history.back()}>← Volver a la Timba</BackLink>
      <H2>🏆 Mis torneos</H2>
      <Muted>Armá un torneo privado o sumate con un link y competí con tus amigos.</Muted>

      <Actions>
        <button onClick={() => { setShowCreate((v) => !v); setShowJoin(false); }}>+ Crear torneo</button>
        <button onClick={() => { setShowJoin((v) => !v); setShowCreate(false); }}>Unirme con link</button>
      </Actions>

      {showCreate && (
        <Form>
          <input value={name} placeholder="Nombre del torneo (ej. Los Pibes)" maxLength={60} autoComplete="off" autoCorrect="off" spellCheck={false} name="timba-tournament-name" onChange={(e) => setName(e.target.value)} />
          <button onClick={create} disabled={name.trim().length < 2}>Crear</button>
        </Form>
      )}
      {showJoin && (
        <Form>
          <input value={joinInput} placeholder="Pegá el link o el código de invitación" autoComplete="off" autoCorrect="off" spellCheck={false} name="timba-join-token" onChange={(e) => setJoinInput(e.target.value)} />
          <button onClick={join} disabled={!joinInput.trim()}>Unirme</button>
        </Form>
      )}

      {mine.length === 0 ? (
        <Empty>Todavía no estás en ningún torneo privado. Creá uno y compartí el link. 🎉</Empty>
      ) : (
        <>
          <Tabs>
            {mine.map((t) => (
              <button key={t.id} className={t.id === selected ? 'on' : ''} onClick={() => setSelected(t.id)}>
                🏆 {t.name}
              </button>
            ))}
          </Tabs>

          {detail && (
            <Board>
              <div className="head">
                <span className="nm">{detail.name}</span>
                <span className="ct">{detail.members} {detail.members === 1 ? 'participante' : 'participantes'}</span>
              </div>
              <TimbaBoard standings={detail.standings} pub={pub} top={3} />
              <button className="share" onClick={share}>Compartir invitación</button>
            </Board>
          )}
        </>
      )}

      {toast && <Toast>{toast}</Toast>}
    </>
  );
}

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 14px 0 10px;
  button {
    background: none;
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 999px;
    padding: 9px 14px;
    font-size: 0.85rem;
    font-weight: 600;
  }
`;

const Form = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  input {
    flex: 1;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 0.9rem;
  }
  button {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
    border-radius: 10px;
    padding: 0 16px;
    font-weight: 700;
  }
  button:disabled { opacity: 0.5; }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin: 8px 0 12px;
  button {
    flex: 0 0 auto;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 999px;
    padding: 7px 14px;
    font-weight: 700;
    font-size: 0.85rem;
    white-space: nowrap;
  }
  button.on { background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText}; border-color: ${({ theme }) => theme.colors.accent}; }
`;

const Empty = styled.p`
  margin: 16px 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Board = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  .head { display: flex; align-items: baseline; justify-content: space-between; padding: 12px 14px; background: ${({ theme }) => theme.colors.surfaceAlt}; }
  .head .nm { font-weight: 800; font-size: 1.05rem; }
  .head .ct { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .share {
    width: 100%;
    background: none;
    color: ${({ theme }) => theme.colors.accent};
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    padding: 12px;
    font-weight: 700;
    font-size: 0.9rem;
  }
`;

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 88px;
  transform: translateX(-50%);
  z-index: 50;
  width: calc(100% - 32px);
  max-width: 380px;
  text-align: center;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 16px;
  padding: 12px 18px;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;
